<ng-container
    *ngIf="{
        plans: plans$ | async,
        currency: invoiceCurrency$ | async,
        planType: planType$ | async,
        subscription: subscription$ | async,
        percentRanges: percentageRanges$ | async,
        subscriptionChangeStep: subscriptionChangeStep$ | async,
        previewSubscriptions: previewSubscriptions$ | async,
        preloading: preloading$ | async,
        subscriptionInvoicePeriod: subscriptionInvoicePeriod$ | async,
    } as $"
    [ngSwitch]="$.subscriptionChangeStep"
>
    <adspt-spinner-overlay
        *ngSwitchCase="'details'"
        [active]="$.preloading"
        class="pricing a-fx-col a-fx-gap-32 a-fx-none"
        [class.pricing-new]="newPlan"
    >
        <div class="a-fx-col a-fx-gap-16 a-fx-none">
            <div class="a-fx-none a-fx a-fx-between-center">
                <div class="h2">
                    {{ 'settings.customer-subscription.pricing-details' | translate }}
                </div>

                <div *ngIf="invoiceCurrencies$ | async as invoiceCurrencies" class="a-fx a-fx-start-center a-fx-gap-8">
                    <div>
                        {{ 'settings.customer-subscription.invoice-currency' | translate }}
                    </div>

                    <div *ngIf="invoiceCurrencies.length <= 1" class="h4">
                        {{ invoiceCurrencies[0] || 'EUR' }}
                    </div>

                    <nz-select
                        *ngIf="invoiceCurrencies.length > 1"
                        [ngModel]="invoiceCurrency$ | async"
                        (ngModelChange)="updateInvoiceCurrency$.next($event)"
                    >
                        <nz-option
                            *ngFor="let currency of invoiceCurrencies$ | async"
                            [nzValue]="currency"
                            [nzLabel]="currency"
                        ></nz-option>
                    </nz-select>
                </div>
            </div>

            <nz-alert
                *ngIf="isLegacySubscription$ | async"
                nzShowIcon
                nzType="warning"
                [nzDescription]="alertDescriptionTmpl"
            >
                <ng-template #alertDescriptionTmpl>
                    <div
                        class="a-text-h4"
                        [innerHTML]="'settings.customer-subscription.legacy-change-plan-title' | translate"
                    ></div>
                    <div [innerHTML]="'settings.customer-subscription.legacy-change-plan-message' | translate"></div>
                </ng-template>
            </nz-alert>

            <div
                [innerHTML]="
                    'settings.customer-subscription.pricing-details-message-' +
                        (newPlan ? 'new' : 'existing') +
                        ((isAgency$ | async) ? '-agency' : '') | translate
                "
            ></div>
        </div>

        <div class="a-fx a-fx-start-between a-fx-none">
            <div class="pricing-period a-fx-none a-fx-col a-fx-gap-8">
                <div
                    *ngIf="
                        $.subscriptionInvoicePeriod !== SubscriptionInvoicePeriod.Yearly && invoicePeriod$
                            | async as invoicePeriod
                    "
                    class="a-fx a-fx-start-center a-fx-gap-8 a-cursor-pointer"
                >
                    <strong
                        [class.a-color-secondary]="invoicePeriod !== SubscriptionInvoicePeriod.Monthly"
                        (click)="updateInvoicePeriod$.next(SubscriptionInvoicePeriod.Monthly)"
                    >
                        {{ 'settings.customer-subscription.pay-monthly' | translate }}
                    </strong>

                    <nz-switch
                        [ngModel]="invoicePeriod === SubscriptionInvoicePeriod.Yearly"
                        (ngModelChange)="
                            updateInvoicePeriod$.next(
                                invoicePeriod === SubscriptionInvoicePeriod.Monthly
                                    ? SubscriptionInvoicePeriod.Yearly
                                    : SubscriptionInvoicePeriod.Monthly
                            )
                        "
                    ></nz-switch>

                    <strong
                        [class.a-color-secondary]="invoicePeriod !== SubscriptionInvoicePeriod.Yearly"
                        (click)="updateInvoicePeriod$.next(SubscriptionInvoicePeriod.Yearly)"
                    >
                        {{ 'settings.customer-subscription.pay-annually' | translate }}
                    </strong>
                </div>

                <div *ngIf="annualDiscountPercentage$ | async as discount" class="a-color-light-blue a-text-cap1">
                    {{
                        'settings.customer-subscription.annual-saving'
                            | translate: { discount: discount | percent: '1.0-0' }
                    }}
                </div>
            </div>

            <div class="a-fx-col a-fx-gap-16 a-fx-flex">
                <nz-radio-group [ngModel]="planType$ | async" (ngModelChange)="updatePlanType$.next($event)">
                    <div class="a-text-align-center a-fx a-fx-around-center">
                        <div
                            *ngFor="let plan of $.plans"
                            nz-radio
                            [nzValue]="plan.type"
                            [nzDisabled]="plan.unavailable"
                            [nz-tooltip]="
                                plan.unavailable
                                    ? ('settings.customer-subscription.unavailable-for-agency' | translate)
                                    : null
                            "
                            class="a-fx a-fx-center-baseline a-fx-flex"
                        >
                            <div
                                class="h3 a-fx-col a-fx-gap-8"
                                [class.a-color-light-blue]="$.planType === plan.type"
                                [class.a-color-disabled]="plan.unavailable"
                            >
                                <span>
                                    {{ 'settings.customer-subscription.plans.' + plan.type | translate }}
                                </span>

                                <span>
                                    {{ plan.price | microsToNumber | currency: $.currency : 'symbol' : '1.0-0' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </nz-radio-group>

                <div class="a-text-cap1 a-text-align-center">
                    {{ 'settings.customer-subscription.pricing-details-percentage-msg' | translate }}
                </div>

                <table class="pricing-table">
                    <thead>
                        <tr>
                            <th>{{ 'settings.customer-subscription.range' | translate }}</th>
                            <th>{{ 'settings.customer-subscription.percentage' | translate }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr *ngFor="let range of $.percentRanges; let index = index">
                            <td>
                                {{
                                    $.percentRanges[index - 1]?.threshold || 0
                                        | microsToNumber
                                        | currency: $.currency : 'symbol' : '1.0-0'
                                }}
                                —
                                {{
                                    range.threshold
                                        ? (range.threshold | microsToNumber | currency: $.currency : 'symbol' : '1.0-0')
                                        : ('settings.customer-subscription.higher' | translate)
                                }}
                            </td>
                            <td>{{ range.percentage | percent: '1.2-2' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="a-fx-col a-fx-gap-16 a-fx-none">
            <adspt-accordion class="pricing-accordion" #examplesAccordion>
                <div *adsptAccordionHeader class="pricing-accordion-header a-fx a-cursor-pointer" adsptAccordionToggle>
                    <div class="h4">
                        {{ 'settings.customer-subscription.pricing-examples' | translate }}
                    </div>

                    <i
                        class="pricing-accordion-caret"
                        [@switch]="examplesAccordion.expanded"
                        [class.a-color-light-blue]="examplesAccordion.expanded"
                    >
                        <adspt-icon name="caret-down" />
                    </i>
                </div>

                <adspt-pricing-examples
                    *adsptAccordionExpandedContent
                    [previewSubscriptions]="previewSubscriptions$ | async"
                    class="pricing-accordion-content"
                ></adspt-pricing-examples>
            </adspt-accordion>

            <adspt-accordion class="pricing-accordion" #compareAccordion>
                <div
                    *adsptAccordionHeader
                    class="pricing-accordion-header a-fx a-fx-start-center a-cursor-pointer"
                    adsptAccordionToggle
                >
                    <div class="h4 a-fx-flex">
                        {{ 'settings.customer-subscription.compare-plans' | translate }}
                    </div>

                    <div
                        *ngFor="let plan of $.plans"
                        [class.a-color-light-blue]="$.planType === plan.type"
                        class="pricing-compare-plan-title a-text-align-center"
                    >
                        {{ 'settings.customer-subscription.plans.' + plan.type | translate }}
                    </div>

                    <i
                        class="pricing-accordion-caret"
                        nz-icon
                        [@switch]="compareAccordion.expanded"
                        [class.a-color-light-blue]="compareAccordion.expanded"
                    >
                        <adspt-icon name="caret-down" />
                    </i>
                </div>

                <adspt-pricing-feature-comparison
                    *adsptAccordionExpandedContent
                    [selectedPlan]="$.planType"
                    [analyticsPrice]="analyticsPrice$ | async"
                    [currency]="$.currency"
                ></adspt-pricing-feature-comparison>
            </adspt-accordion>
        </div>
    </adspt-spinner-overlay>

    <adspt-subscription-downgrade-dialog
        *ngSwitchCase="'downgrade'"
        [currentPlan]="$.subscription"
        [newPlan]="(previewSubscription$ | async) || $.subscription"
        [downgradeSteps]="downgradeSteps"
        [analyticsPrice]="analyticsPrice$ | async"
        [currency]="$.currency"
    />

    <adspt-subscription-success *ngSwitchCase="'confirmation'" />
</ng-container>
